import React, { useMemo, useState } from 'react';
import { Field, FieldTypes, HealthPlanStatuses, Inscription, LabelWithSubfield } from '@common-fe/common-fe';
import * as yup from 'yup';

import { VALIDATORS } from '@/common';
import { REQUIRED_TEXT, YES_KEY } from '@/common/constants.ts';
import { WarnModal } from '@/components/elements';
import { ModalWrapper } from '@/components/wrappers';
import CreateCarrierForm from '@/modules/CarrierConfig/components/CreateCarrierConfig/CreateCarrierConfigForm/CreateCarrierForm';
import { useGetCarrierByIdQuery } from '@/modules/CarrierConfig/components/CreateCarrierConfig/CreateCarrierConfigForm/OrgLevelSelector/useGetCarrierById.query';
import useGetCarriersQuery, {
  Carrier,
} from '@/modules/CarrierConfig/components/CreateCarrierConfig/CreateCarrierConfigForm/OrgLevelSelector/useGetCarriers.query';
import { HEALTH_PLAN_SETUP_FIELD } from '@/modules/HealthPlan/HealthPlanSetup/HealthPlanSetup.constants';
import { useHealthPlanFieldsState, useTemplateFieldState } from '@/modules/HealthPlan/HealthPlanSetup/hooks';
import { usePreviewStore } from '@/modules/HealthPlan/HealthPlanSetup/stores';
import { useHealthPlanTypes } from '@/modules/HealthPlan/hooks';
import { useHealthTemplatesListQuery } from '@/modules/HealthPlan/queries';
import { ExpandedOption } from '@/modules/HealthPlan/queries/usePlansByPlanYear.query.ts';
import theme from '@/styles/theme';
import { useFieldsWithDefaultValues } from '@/utils';

import { useHealthPlanNameStore } from '../stores';

const MAX_SEARCH_VALUE = 3;
const TRUE_KEY = 'true';

interface HealthPlanNameFieldsProps {
  isActivationMode?: boolean;
  isTemplateMode?: boolean;
  isEditMode?: boolean;
  arePlansEditable?: boolean;
  isAdditionalSection?: boolean;
  planYearFormatedOptions?: ExpandedOption[];
}

const useHealthPlanNameFields = ({
  isActivationMode,
  isTemplateMode,
  isEditMode,
  arePlansEditable,
  isAdditionalSection,
  planYearFormatedOptions,
}: HealthPlanNameFieldsProps) => {
  const [searchedCarrierName, setSearchedCarrierName] = useState('');
  const [createdCarrier, setCreatedCarrier] = useState<Carrier>();
  const [isVisible, setIsVisible] = useState(false);
  const [isSuccessModalVisible, setIsSuccessModalVisible] = useState(false);

  const sourceState = useHealthPlanNameStore((state) => state.sourceState);
  const currentState = useHealthPlanNameStore((state) => state.state);
  const previewMode = usePreviewStore((state) => state.previewMode);
  const appliedSourceState = useMemo(() => {
    if (previewMode) {
      return currentState;
    }
    return sourceState;
  }, [currentState, previewMode, sourceState]);

  const { carriers, isLoading } = useGetCarriersQuery(
    searchedCarrierName?.length >= MAX_SEARCH_VALUE ? searchedCarrierName : undefined
  );

  const currentCarrier = useMemo(() => {
    if (currentState?.carrierName) {
      return carriers.find((carrierEl) => carrierEl.id === currentState.carrierName);
    }
    return null;
  }, [carriers, currentState]);

  const { data } = useGetCarrierByIdQuery(currentState.carrierName);
  const receivedCarrier = useMemo(() => {
    if (data) {
      return {
        id: `${data.id || ''}`,
        name: data.name,
        externalId: data.external_id,
      } as Carrier;
    }
    return undefined;
  }, [data]);


  const options = useMemo(
    () =>
      [
        ...(createdCarrier
          ? [createdCarrier, ...carriers]
          : receivedCarrier && !carriers.find((carrierEl) => carrierEl.id === currentState.carrierName)
            ? [receivedCarrier, ...carriers]
            : carriers),
      ].map((carrierEl) => ({ key: carrierEl.id, title: carrierEl.name })),
    [carriers, receivedCarrier, createdCarrier, currentState.carrierName]
  );

  const carrierExternalId = useMemo(
    () => createdCarrier?.externalId || currentCarrier?.externalId || receivedCarrier?.externalId,
    [createdCarrier, receivedCarrier, currentCarrier]
  );

  const healthPlanTypes = useHealthPlanTypes();
  const { options: formattedTemplatesOptions } = useHealthTemplatesListQuery({
    status: HealthPlanStatuses.ACTIVE,
  });

  const onlyActivationRequired = useMemo(
    () => (isActivationMode ? VALIDATORS.REQUIRED_STRING : VALIDATORS.STRING),
    [isActivationMode]
  );
  const hasLinkedPlans = useMemo(
    () => !!(currentState.plans && currentState.plans.length > 0),
    [currentState.plans]
  );

  const createCarrierModals = useMemo(
    () => (
      <>
        <ModalWrapper
          testId="create_carrier"
          visible={isVisible}
          title="Create Carrier"
          onSetVisible={setIsVisible}
        >
          <CreateCarrierForm
            onClose={() => setIsVisible(false)}
            onSuccess={(carrierEl: Carrier) => {
              setCreatedCarrier(carrierEl);
              setIsVisible(false);
              setIsSuccessModalVisible(true);
            }}
          />
        </ModalWrapper>
        <WarnModal
          testId="carrier_created"
          visible={isSuccessModalVisible}
          onSetVisible={setIsSuccessModalVisible}
          header={`The ${createdCarrier?.name} carrier has been created!`}
          helptext="You can now use this new carrier for the Health Plan template configuration in the previous window."
          isSuccessIcon
          submitButtonText="Close"
          onSubmit={() => setIsSuccessModalVisible(false)}
        />
      </>
    ),
    [createdCarrier, isSuccessModalVisible, isVisible]
  );

  const fields = useMemo<Field[]>(() => {
    const additionalFields = [
      {
        name: HEALTH_PLAN_SETUP_FIELD.CARRIER_NAME,
        type: FieldTypes.AutocompleteDropdown,
        singleMode: true,
        label: 'Carrier name',
        showRequireIcon: isTemplateMode,
        disabled: !isTemplateMode,
        options,
        placeholder: 'Select carrier name',
        validator: onlyActivationRequired,
        completeValue: createdCarrier?.id,
        resetCompleteValue: () => setCreatedCarrier(undefined),
        add: isTemplateMode ? () => setIsVisible(true) : undefined,
        isLoading,
        onSearch: setSearchedCarrierName,
        onClear: () => setCreatedCarrier(undefined),
      },
      {
        placeholder: 'Select carrier ID',
        name: HEALTH_PLAN_SETUP_FIELD.CARRIER_ID,
        showRequireIcon: isTemplateMode,
        type: FieldTypes.Node,
        label: 'Carrier ID',
        value: <Inscription>{carrierExternalId || 'Carrier ID'}</Inscription>,
      },
      {
        name: HEALTH_PLAN_SETUP_FIELD.HEALTH_PLAN_TYPE,
        type: FieldTypes.MultipleDropdown,
        value: currentState?.healthPlanTypes,
        label: 'Health plan type',
        showRequireIcon: true,
        options: healthPlanTypes,
        placeholder: 'Select health plan type',
        validator: VALIDATORS.REQUIRED_ARRAY,
      },
      ...(!isTemplateMode
        ? [
            {
              name: HEALTH_PLAN_SETUP_FIELD.PLANS,
              type: FieldTypes.CheckboxDropdown,
              disabled: !arePlansEditable,
              fieldHintText: !arePlansEditable
                ? 'There are employees who have already been enrolled in these plans'
                : '',
              placeholder: 'Select a plan',
              options: planYearFormatedOptions || [],
              label: 'Linked plans',
              emptyListMessage: 'No plans have been found',
              validator: VALIDATORS.ARRAY,
            } as Field,
          ]
        : []),
      ...(hasLinkedPlans
        ? [
            {
              name: HEALTH_PLAN_SETUP_FIELD.AUTOCREATE_CDH_ENROLLMENT,
              type: FieldTypes.Radio,
              label: 'Autocreate CDH enrollment',
              showRequireIcon: true,
              disabled: !arePlansEditable,
              fill: true,
              fieldHintText: !arePlansEditable
                ? 'There are employees who have already been enrolled in these plans'
                : '',
              options: [
                {
                  label: 'Yes',
                  value: TRUE_KEY,
                },
                {
                  label: 'No',
                  value: 'false',
                },
              ],
              validator: yup.string().test({
                test: () => !!currentState.autocreateCdhEnrollment,
                message: REQUIRED_TEXT,
              }),
            } as Field,
            ...(!isTemplateMode
              ? [
                  {
                    name: HEALTH_PLAN_SETUP_FIELD.PER_PERSON_SPEND_LIMIT,
                    type: FieldTypes.Radio,
                    label: 'Per person spend limit',
                    options: [
                      { label: 'Yes', value: YES_KEY },
                      { label: 'No', value: 'no' },
                    ],
                    validator: VALIDATORS.STRING,
                  } as Field,
                ]
              : []),
            ...(!isTemplateMode
              ? [
                  {
                    name: HEALTH_PLAN_SETUP_FIELD.PER_CATEGORY_SPEND_LIMIT,
                    type: FieldTypes.Radio,
                    label: 'Per spend category spend limit',
                    options: [
                      { label: 'Yes', value: YES_KEY },
                      { label: 'No', value: 'no' },
                    ],
                    validator: VALIDATORS.STRING,
                  } as Field,
                ]
              : []),
          ]
        : []),
    ] as Field[];
    if (isTemplateMode) {
      return [
        {
          name: HEALTH_PLAN_SETUP_FIELD.HEALTH_PLAN_NAME,
          type: FieldTypes.Text,
          label: 'Health plan template name',
          showRequireIcon: true,
          placeholder: 'Enter health plan template name',
          validator: VALIDATORS.REQUIRED_STRING,
        },
        {
          name: HEALTH_PLAN_SETUP_FIELD.PLAN_SUMMARY,
          type: FieldTypes.TextArea,
          label: (
            <LabelWithSubfield
              title="Summary"
              desc="Summary of the plan to display to employer admins who set up their organizations within your organization."
            />
          ),
          placeholder: 'Summary',
          maxLength: 500,
          style: { minHeight: '120px' },
        },
        {
          name: HEALTH_PLAN_SETUP_FIELD.HEALTH_PLAN_TEMPLATE,
          type: FieldTypes.Dropdown,
          label: 'Parent template',
          disabled: true,
          options: formattedTemplatesOptions,
          placeholder: 'Select a health plan template',
          validator: VALIDATORS.STRING,
        },
        ...additionalFields,
      ];
    }
    if (isAdditionalSection) {
      return additionalFields;
    }
    return [
      {
        name: HEALTH_PLAN_SETUP_FIELD.HEALTH_PLAN_NAME,
        type: FieldTypes.Text,
        label: 'Health plan name',
        showRequireIcon: true,
        placeholder: 'Enter health plan template name',
        validator: VALIDATORS.REQUIRED_STRING,
      },
      {
        name: HEALTH_PLAN_SETUP_FIELD.HEALTH_PLAN_NOTES,
        type: FieldTypes.TextArea,
        label: <LabelWithSubfield title="Health plan notes" />,
        placeholder: 'Notes',
        maxLength: 2000,
        withParagraphs: true,
        style: {
          minHeight: isEditMode ? '120px' : 'auto',
          padding: `${theme.spacings.l} 0`,
        },
      },
      {
        name: HEALTH_PLAN_SETUP_FIELD.PLAN_CODE,
        type: FieldTypes.Text,
        label: 'Health plan code',
        showRequireIcon: true,
        placeholder: 'Enter plan code',
        // validator: yup.string().trim().test({
        //   test: (val) => val !== errors?.planCode,
        //   message: 'This plan code is already used',
        // }).required(REQUIRED_TEXT),
        validator: !isTemplateMode && onlyActivationRequired,
      },
      {
        name: HEALTH_PLAN_SETUP_FIELD.HEALTH_PLAN_TEMPLATE,
        type: FieldTypes.Dropdown,
        label: 'Parent template',
        disabled: true,
        options: formattedTemplatesOptions,
        showRequireIcon: true,
        placeholder: 'Select a health plan template',
        validator: VALIDATORS.STRING,
      },
    ];
  }, [
    isTemplateMode,
    options,
    onlyActivationRequired,
    createdCarrier?.id,
    isLoading,
    carrierExternalId,
    currentState?.healthPlanTypes,
    currentState.autocreateCdhEnrollment,
    healthPlanTypes,
    arePlansEditable,
    planYearFormatedOptions,
    hasLinkedPlans,
    isAdditionalSection,
    isEditMode,
    formattedTemplatesOptions,
  ]);
  const filteredField = useHealthPlanFieldsState(fields);

  const formatedFields = useFieldsWithDefaultValues(filteredField, appliedSourceState);
  const stateFields = useTemplateFieldState(formatedFields, isTemplateMode, isEditMode);

  return { stateFields, createCarrierModals };
};

export default useHealthPlanNameFields;
